import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Stack,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  Avatar,
  Divider,
  Box
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { usePermissions } from '../../utils/permissions';
import { supabase } from '../../supabaseClient';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { HeaderCard, SectionPaper } from './accounting-overview-components/layout';
import BankAccountTransactions from './accounting-banking-components/BankAccountTransactions';

const accounts = [
  { bank: 'First National', type: 'Operating', balance: '$1,240,890', sync: 'Realtime', variance: '+$18,200' },
  { bank: 'Mercury Treasury', type: 'Reserves', balance: '$890,450', sync: 'Synced', variance: '+$4,120' },
  { bank: 'Silicon Hills', type: 'Payroll', balance: '$215,680', sync: 'Queued', variance: '-$12,400' },
  { bank: 'Stripe Capital', type: 'Collections', balance: '$122,900', sync: 'Realtime', variance: '+$8,760' }
];

const timeline = [
  { label: 'Stripe payouts', detail: '$86,200 arriving tomorrow' },
  { label: 'Wire clearances', detail: '$31,400 pending compliance' },
  { label: 'FX conversion', detail: '€22,000 auto-convert Fri' }
];

const AccountingBankingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { canViewAccountingBanking } = usePermissions();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [companyName, setCompanyName] = useState('');

  const fetchCompanyData = async () => {
    if (!companyId) return;
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  // Permission check
  if (!canViewAccountingBanking()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Banking Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Banking. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
  };

  // If an account is selected, show the transactions view
  if (selectedAccount) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
        <BankAccountTransactions account={selectedAccount} onBack={handleBackToAccounts} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <HeaderCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 400, color: 'primary.main', mb: 0.5 }}>
                Banking
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem',
                mb: 1
              }}>
                {companyName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Connected institutions, reconciliation status, and health signals. Dummy numbers mimic a live setup so the workflows feel ready.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" startIcon={<AccountBalanceIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}>
                Link Account
              </Button>
              <Button variant="outlined" startIcon={<SyncIcon />} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}>
                Refresh Feeds
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} md={8}>
          <SectionPaper>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                Connected Accounts
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Export
              </Button>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Institution</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Sync State</TableCell>
                  <TableCell align="right">Variance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow
                    key={account.bank}
                    hover
                    onClick={() => handleAccountClick(account)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        transform: 'translateX(4px)',
                        boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`
                      }
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 32, height: 32 }}>
                          <AccountBalanceIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {account.bank}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {account.type}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{account.type}</TableCell>
                    <TableCell>{account.balance}</TableCell>
                    <TableCell>
                      <Chip
                        label={account.sync}
                        size="small"
                        color={account.sync === 'Queued' ? 'warning' : 'success'}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: account.variance.startsWith('-') ? theme.palette.error.main : theme.palette.success.main }}>
                      {account.variance}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <SectionPaper>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
                Cash Health Score
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontWeight: 400, color: theme.palette.success.main }}>
                  92
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Excellent
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                {[
                  { label: 'Liquidity', detail: '126% of plan' },
                  { label: 'Bank redundancy', detail: '4 linked institutions' },
                  { label: 'Daily reconciled', detail: '97% matched' }
                ].map((item) => (
                  <Stack direction="row" spacing={1.5} key={item.label} alignItems="center">
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.detail}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </SectionPaper>

            <SectionPaper>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
                Settlement Timeline
              </Typography>
              <Stack spacing={1.5}>
                {timeline.map((event) => (
                  <Card
                    key={event.label}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      backgroundColor: alpha(theme.palette.grey[50], 0.7),
                      p: 2
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {event.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {event.detail}
                    </Typography>
                  </Card>
                ))}
              </Stack>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 400 }}
              >
                View Cash Calendar
              </Button>
            </SectionPaper>
          </Stack>
        </Grid>
      </Grid>

      <SectionPaper sx={{ mt: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row'}} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 400}}>
              Reconciliation Queue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sample of unmatched items grouped by bank feed
            </Typography>
          </Box>
          <Chip label="3 items" color="warning" variant="outlined" />
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bank</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { bank: 'First National', desc: 'Wire - vendor tooling', date: 'Nov 22', amount: '$12,400', status: 'Needs review' },
              { bank: 'Stripe Capital', desc: 'Card payout', date: 'Nov 21', amount: '$8,760', status: 'Auto-match pending' },
              { bank: 'Silicon Hills', desc: 'Payroll debit', date: 'Nov 21', amount: '$49,300', status: 'Waiting approval' }
            ].map((row) => (
              <TableRow key={row.desc} hover>
                <TableCell>{row.bank}</TableCell>
                <TableCell>{row.desc}</TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right">{row.amount}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={row.status === 'Needs review' ? 'warning' : row.status === 'Waiting approval' ? 'info' : 'default'}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionPaper>

      <SectionPaper>
        <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
          Bank Policies
        </Typography>
        <Grid container spacing={2}>
          {[
            { title: 'Dual approvals', detail: 'Required above $50k', status: 'Active' },
            { title: 'FX hedge', detail: 'EUR 75% covered', status: 'Monitoring' },
            { title: 'ACH whitelist', detail: '12 vendors enrolled', status: 'Active' }
          ].map((policy) => (
            <Grid item xs={12} md={4} key={policy.title}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                  p: 2,
                  height: '100%'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {policy.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {policy.detail}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Chip
                  size="small"
                  label={policy.status}
                  color={policy.status === 'Monitoring' ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </SectionPaper>
    </Container>
  );
};

export default AccountingBankingPage;

